import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";

export default function useReviewData({
  selectedUser = null,
  user = null,
  isAdmin = false,
  selectedProperty = null,
  setStatusMessage = null,
} = {}) {
  const [reviews, setReviews] = useState([]);
  const [myReceivedReviews, setMyReceivedReviews] = useState([]);
  const [publicFeedUserReviews, setPublicFeedUserReviews] = useState([]);
  const [propertyReviews, setPropertyReviews] = useState([]);
  const [allPropertyReviews, setAllPropertyReviews] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [reportedReviews, setReportedReviews] = useState([]);

  useEffect(() => {
    if (!selectedUser?.uid) {
      setReviews([]);
      return;
    }

    const q = query(
      collection(db, "reviews"),
      where("targetUserId", "==", selectedUser.uid)
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const visibleItems = items.filter((item) => item.status === "approved");

        visibleItems.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        setReviews(visibleItems);
      },
      (error) => {
        console.error("Reviews listener error:", error);
        if (typeof setStatusMessage === "function") {
          setStatusMessage(
            "Reviews listener error. If Firebase asks for an index, click the index link in the console error."
          );
        }
      }
    );

    return () => unsub();
  }, [selectedUser?.uid, setStatusMessage]);

  useEffect(() => {
    if (!user?.uid) {
      setMyReceivedReviews([]);
      return;
    }

    const q = query(
      collection(db, "reviews"),
      where("targetUserId", "==", user.uid)
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .filter((item) => item.status === "approved");

        items.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        setMyReceivedReviews(items);
      },
      (error) => {
        console.error("My received reviews listener error:", error);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "reviews"),
      (snapshot) => {
        const items = snapshot.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .filter((item) => item.status === "approved")
          .sort(
            (a, b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0)
          );

        setPublicFeedUserReviews(items);
      },
      (error) => {
        console.error("Public feed reviews listener error:", error);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "propertyReviews"),
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        items.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        setAllPropertyReviews(items);
      },
      (error) => {
        console.error("All property reviews listener error:", error);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!selectedProperty?.id) {
      setPropertyReviews([]);
      return;
    }

    const q = query(
      collection(db, "propertyReviews"),
      where("propertyId", "==", selectedProperty.id)
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        items.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        setPropertyReviews(items);
      },
      (error) => {
        console.error("Property reviews listener error:", error);
      }
    );

    return () => unsub();
  }, [selectedProperty?.id]);

  useEffect(() => {
    if (!isAdmin) {
      setPendingReviews([]);
      setReportedReviews([]);
      return;
    }

    const unsub = onSnapshot(
      collection(db, "reviews"),
      (snapshot) => {
        const all = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        all.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        setPendingReviews(all.filter((r) => r.status === "pending"));
        setReportedReviews(
          all.filter((r) => r.reported === true && r.status !== "deleted")
        );
      },
      (error) => {
        console.error("Admin reviews listener error:", error);
      }
    );

    return () => unsub();
  }, [isAdmin]);

  return {
    reviews,
    setReviews,
    myReceivedReviews,
    setMyReceivedReviews,
    publicFeedUserReviews,
    setPublicFeedUserReviews,
    propertyReviews,
    setPropertyReviews,
    allPropertyReviews,
    setAllPropertyReviews,
    pendingReviews,
    setPendingReviews,
    reportedReviews,
    setReportedReviews,
  };
}
